import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import Breadcrumb from "../../components/Breadcrumb";
import PageHeader from "../../components/common/PageHeader";
import ViewProductAffiliate from "../../components/products/ViewProductAffiliate";
import MasterLayout from "../../masterLayout/MasterLayout";
import { API_URL } from "../../config/api";

const SellerProductsPage = () => {
  const [sellerProducts, setSellerProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getSellerProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/seller/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      // Se resposta não é ok, tratar mensagem do backend
      if (!response.ok) {
        // 404 ou 403 - mostrar mensagem do backend ou mensagem padrão
        if (response.status === 404 || response.status === 403) {
          setError(data.message || 'Você ainda não tem produtos aprovados');
          setSellerProducts([]);
          setLoading(false);
          return;
        }
        throw new Error(data.message || `Erro ${response.status}: ${response.statusText}`);
      }
      
      if (data && data.success) {
        setSellerProducts(data.data || []);
      } else {
        setError(data.message || 'Resposta inválida da API');
        setSellerProducts([]);
      }
    } catch (error) {
      console.error("Erro ao buscar produtos do seller:", error);
      setError(error.message || 'Erro ao carregar produtos');
      setSellerProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoizar o componente ViewProductAffiliate
  const viewProductComponent = useMemo(() => (
    <ViewProductAffiliate affiliatesProducts={sellerProducts} />
  ), [sellerProducts]);

  useEffect(() => {
    getSellerProducts();
  }, [getSellerProducts]);

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb title="Produtos" />
        <PageHeader
          icon="mdi:package-variant"
          title="Produtos"
          subtitle="Produtos aprovados que você pode vender"
        />
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="text-muted">Carregando produtos...</p>
        </div>
      </MasterLayout>
    );
  }

  // Se há erro, mas não é erro crítico (pode ser que não há produtos)
  if (error && !sellerProducts.length && !loading) {
    // Se o erro indica que não há produtos, mostrar mensagem amigável
    if (error.includes('não tem') || error.includes('não tem produtos') || error.includes('aprovados')) {
      return (
        <MasterLayout>
          <Breadcrumb title="Produtos" />
          <PageHeader
            icon="mdi:package-variant"
            title="Produtos"
            subtitle="Produtos aprovados que você pode vender"
          />
          <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
              <div className="text-center py-5">
                <Icon icon="mdi:package-variant-outline" className="fs-1 text-muted mb-3" />
                <h6 className="text-muted">Nenhum produto aprovado</h6>
                <p className="text-muted">
                  {error}
                </p>
                <button 
                  className="btn btn-primary btn-sm mt-2"
                  onClick={getSellerProducts}
                >
                  Atualizar
                </button>
              </div>
            </div>
          </div>
        </MasterLayout>
      );
    }
    
    // Erro crítico
    return (
      <MasterLayout>
        <Breadcrumb title="Produtos" />
        <PageHeader
          icon="mdi:package-variant"
          title="Produtos"
          subtitle="Produtos aprovados que você pode vender"
        />
        <div className="alert alert-danger" role="alert">
          <h6 className="alert-heading">Erro ao carregar produtos</h6>
          <p className="mb-0">{error}</p>
          <button 
            className="btn btn-outline-danger btn-sm mt-2"
            onClick={getSellerProducts}
          >
            Tentar novamente
          </button>
        </div>
      </MasterLayout>
    );
  }

  return (
    <>
      {/* MasterLayout */}
      <MasterLayout>
        {/* Breadcrumb */}
        <Breadcrumb title="Produtos" />

        {/* Header Section */}
        <PageHeader
          icon="mdi:package-variant"
          title="Produtos"
          subtitle="Produtos aprovados que você pode vender"
        />

        {/* ViewProduct */}
        {viewProductComponent}
      </MasterLayout>
    </>
  );
};

export default SellerProductsPage;

